/* global React, Icon, Button, Modal, Segmented, toast, fmt, PlummeI18n */
// ============================================================
// AI Rådgiver — rådgiverprofil og oppgavesenter
// ============================================================
// Profilen er brukerens preferanser, ikke regnskapsdata. Den lagres via backend og
// brukes som kontekst til rådgiveren. Alt som vises som «lært» eller «planlagt» leses
// ut av data brukeren allerede har registrert — profilen finner ikke på noe selv.
const { useState: profUseState } = React;

// Valgene eies av backend, som også avgjør hvilke mål som gir mening for foretaket —
// utbytte finnes ikke for et enkeltpersonforetak. Fallbacken brukes bare hvis backend
// ikke er lastet, og speiler samme liste.
const AII_PROFILE_FALLBACK = {
  mainGoal: ['revenue', 'profitability', 'buffer', 'hiring', 'investment', 'workload'],
  risk: ['cautious', 'balanced', 'offensive'],
  cadence: ['daily', 'weekly', 'monthly'],
};
const AII_PROFILE_FIELD_KEYS = ['mainGoal', 'risk', 'cadence'];

// Sesongmønsteret er ikke lagret noe sted — det leses direkte ut av kontantstrømmen,
// så det aldri kan komme i utakt med tallene.
const aiiSeasonPattern = (forecast) => {
  const months = (Array.isArray(forecast) ? forecast : []).filter((point) => Number(point.inn) > 0);
  if (months.length < 3) return null;
  const sorted = [...months].sort((a, b) => Number(b.inn) - Number(a.inn));
  return {
    strong: sorted.slice(0, 2).map((point) => point.m),
    weak: sorted.slice(-2).map((point) => point.m).reverse(),
  };
};

// Each field says what it actually changes. The texts describe only what is wired:
// ranking and AI context (mainGoal, risk), how often the daily insight is generated
// (cadence) and the accountant label plus prompt instruction (accountantInvolved).
function AiiProfileSelect({ fieldKey, options, value, onChange, busy }) {
  return <label className="aii-profile-field">
    <span>{t('adv.profile.' + fieldKey)}</span>
    <select className="input" value={value} disabled={busy} onChange={(e) => onChange(fieldKey, e.target.value)}>
      {options.map((option) => <option key={option} value={option}>{t('adv.profile.' + fieldKey + '.' + option)}</option>)}
    </select>
    <small className="t-xs muted">{t('adv.profile.effect.' + fieldKey)}</small>
  </label>;
}

// Shown on tax, VAT, year-end and filing advice when the owner wants the accountant
// involved (PlummeAiAdvisorTasks.applyAdvisorProfile sets the flag).
function AiiAccountantTag() {
  return <span className="aii-effect-tag" data-help-title={t('adv.profile.discussWithAccountant')} data-help={t('adv.profile.accountantNote')}>
    <Icon name="users" size={11} />{t('adv.profile.discussWithAccountant')}
  </span>;
}

function AiiAdvisorProfileDrawer({ open, onClose, forecast, bufferControl }) {
  const [profile, setProfile] = profUseState(() => window.PlummeBackend?.getAiAdvisorProfile?.() || null);
  const [busy, setBusy] = profUseState(false);
  React.useEffect(() => {
    if (open) setProfile(window.PlummeBackend?.getAiAdvisorProfile?.() || null);
  }, [open]);
  if (!open || !profile) return null;
  const options = window.PlummeBackend?.getAiAdvisorProfileOptions?.() || AII_PROFILE_FALLBACK;
  const events = window.PlummeBackend?.getPlannedCashEvents?.() || [];
  const dismissLog = window.PlummeBackend?.getAiAdvisorDismissLog?.() || [];
  const season = aiiSeasonPattern(forecast);
  const save = async (key, value) => {
    const previous = profile;
    setProfile((current) => ({ ...current, [key]: value }));
    setBusy(true);
    try {
      const next = await window.PlummeBackend?.saveAiAdvisorProfile?.({ [key]: value });
      if (next) setProfile(next);
    } catch (error) {
      setProfile(previous);
      toast(error?.message || t('adv.profile.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };
  return <Modal open onClose={onClose} title={t('adv.profile.title')} max={560}
    footer={<Button onClick={onClose} style={{ width: '100%' }}>{t('common.close')}</Button>}>
    <div className="aii-drawer-sec">
      <p className="t-sm muted">{t('adv.profile.intro')}</p>
      {bufferControl && <div className="aii-profile-field"><span data-help={t('helpTopics.buffer.body')} data-help-title={t('helpTopics.buffer.title')}>{t('adv.profile.buffer')}</span>{bufferControl}</div>}
      {AII_PROFILE_FIELD_KEYS.map((fieldKey) => <AiiProfileSelect key={fieldKey} fieldKey={fieldKey}
        options={options[fieldKey] || AII_PROFILE_FALLBACK[fieldKey]} value={profile[fieldKey]} onChange={save} busy={busy} />)}
      <label className="aii-profile-field row-check">
        <input type="checkbox" checked={profile.accountantInvolved} disabled={busy}
          onChange={(e) => save('accountantInvolved', e.target.checked)} />
        <span>{t('adv.profile.accountantInvolved')}</span>
      </label>
      <small className="t-xs muted">{t('adv.profile.effect.accountantInvolved')}</small>
      <div className="aii-profile-field">
        <span>{t('adv.profile.investments')}</span>
        {events.length
          ? <ul className="aii-profile-list">{events.map((event) => <li key={event.id}>
            {event.label} — {fmt(Math.abs(event.amount))} ({PlummeI18n.date(event.date, { day: 'numeric', month: 'short', year: 'numeric' })})
          </li>)}</ul>
          : <p className="t-xs muted">{t('adv.profile.noInvestments')}</p>}
      </div>
      <div className="aii-profile-field">
        <span>{t('adv.profile.season')}</span>
        {season
          ? <p className="t-xs ink-2">{t('adv.profile.seasonText', { strong: season.strong.join(', '), weak: season.weak.join(', ') })}</p>
          : <p className="t-xs muted">{t('adv.profile.noSeason')}</p>}
      </div>
      <div className="aii-profile-field">
        <span>{t('adv.profile.learned')}</span>
        {dismissLog.length
          ? <ul className="aii-profile-list">{dismissLog.map((entry, i) => <li key={i}>{t('adv.rec.' + entry.recId, {}) || entry.recId} — «{entry.reason}»</li>)}</ul>
          : <p className="t-xs muted">{t('adv.profile.nothingLearned')}</p>}
      </div>
    </div>
  </Modal>;
}

function AiiAdvisorProfileButton({ forecast, bufferControl }) {
  const [open, setOpen] = profUseState(false);
  return <React.Fragment>
    <button type="button" className="aii-profile-btn" onClick={() => setOpen(true)}
      data-help-title={t('adv.profile.title')} data-help={t('adv.profile.help')}>
      <Icon name="sliders" size={15} />{t('adv.profile.title')}
    </button>
    <AiiAdvisorProfileDrawer open={open} onClose={() => setOpen(false)} forecast={forecast} bufferControl={bufferControl} />
  </React.Fragment>;
}

// ============================================================
// Oppgavesenter — «N ting å gjøre i dag»
// ============================================================
// Samme anbefalinger som kortene på Oversikt, gruppert på status i stedet for
// viktighet: det brukeren trenger å vite er hva som fortsatt venter på hen.
const AII_TASK_BUCKETS = ['now', 'planned', 'closed'];
function AiiTaskCenterModal({ open, onClose, buckets, onAct }) {
  const [filter, setFilter] = profUseState('alle');
  const [scope, setScope] = profUseState('now');
  React.useEffect(() => { if (open) { setFilter('alle'); setScope('now'); } }, [open]);
  if (!open) return null;
  const areas = [{ value: 'alle', label: t('adv.taskcenter.all') }]
    .concat(['liquidity', 'cost', 'tax', 'risk', 'income']
      .filter((type) => AII_TASK_BUCKETS.some((bucket) => (buckets[bucket] || []).some(({ rec }) => rec.effectType === type)))
      .map((type) => ({ value: type, label: t('adv.effect.' + type) })));
  const matches = (rec) => filter === 'alle' || rec.effectType === filter;
  const rows = AII_TASK_BUCKETS
    .filter((bucket) => scope === 'all' || bucket === scope)
    .map((bucket) => [bucket, (buckets[bucket] || []).filter(({ rec }) => matches(rec))])
    .filter(([, list]) => list.length);
  return <Modal open portal center onClose={onClose} className="aii-work-modal" title={t('adv.taskcenter.title')}>
    <div className="aii-taskcenter">
      <Segmented value={scope} onChange={setScope} options={[
        { value: 'now', label: t('adv.taskcenter.current', { count: (buckets.now || []).length }) },
        { value: 'all', label: t('adv.taskcenter.history') },
      ]} />
      {areas.length > 1 && <Segmented value={filter} onChange={setFilter} options={areas} />}
      <div className="aii-taskcenter-groups">
        {rows.map(([bucket, list]) => <div key={bucket} className="aii-taskgroup">
          <div className="aii-taskgroup-lbl">{t('adv.status.bucket.' + bucket)}<span>{list.length}</span></div>
          <div className="aii-taskgroup-rows">
            {list.map(({ rec, resolved }) => <button key={rec.id} type="button" className="aii-task-row"
              onClick={() => { onClose(); onAct && onAct(rec); }}>
              <span className="aii-task-ico"><Icon name={rec.icon} size={16} /></span>
              <span className="aii-task-body">
                <span className="aii-task-title">{rec.title || rec.text}</span>
                {resolved?.note && <span className="aii-task-sub">{resolved.note}</span>}
                {rec.discussWithAccountant && <span className="aii-task-sub">{t('adv.profile.discussWithAccountant')}</span>}
              </span>
              {window.AiiStatusPill && resolved && <window.AiiStatusPill resolved={resolved} />}
              <Icon name="chevRight" size={15} className="aii-task-chev" />
            </button>)}
          </div>
        </div>)}
        {!rows.length && <p className="t-sm muted">{t('adv.taskcenter.empty')}</p>}
      </div>
    </div>
  </Modal>;
}

Object.assign(window, { AiiAdvisorProfileButton, AiiAdvisorProfileDrawer, AiiTaskCenterModal, AiiAccountantTag, aiiSeasonPattern });
